import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  Suspense,
  lazy,
  useLayoutEffect,
  useRef,
} from "react";
import {
  CCard,
  CCardHeader,
  CCardBody,
  CButton,
  CTab,
  CTabs,
  CTabContent,
  CTabPanel,
  CTabList,
  CSpinner,
  CRow,
  CCol,
} from "@coreui/react";
import { apiClient } from "services/Api";
import CIcon from "@coreui/icons-react";
import { cilInfo } from "@coreui/icons";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/gantt";
import "highcharts/modules/exporting";
import "highcharts/modules/offline-exporting";

import LoadingScreen from "components/globals/LoadingScreen";
import { getGanttOptions } from "./utils";
import FilterPanel from "./FilterPanel";
import { Tour } from "@reactour/tour";
import { getProjectTabTourSteps } from "./tourSteps";

const ActivitiesANI = lazy(() => import("./pages/ani/Activities"));
const TeamANI = lazy(() => import("./pages/ani/Team"));
const SheetANI = lazy(() => import("./pages/ani/Sheet"));
const ImputationANI = lazy(() => import("./pages/ani/Imputation"));

const ActivitiesIAPMEI = lazy(() => import("./pages/iapmei/Activities"));
const TeamIAPMEI = lazy(() => import("./pages/iapmei/Team"));
const SheetIAPMEI = lazy(() => import("./pages/iapmei/Sheet"));
const ImputationIAPMEI = lazy(() => import("./pages/iapmei/Imputation"));

const ActivitiesOTHER = lazy(() => import("./pages/other/Activities"));
const TeamOTHER = lazy(() => import("./pages/other/Team"));
const SheetOTHER = lazy(() => import("./pages/other/Sheet"));
const ImputationOTHER = lazy(() => import("./pages/other/Imputation"));

const TeamSalaries = lazy(() =>
  import("views/roberto/pages/shared/teamSalaries/TeamSalariesPage"),
);

const Project = ({
  clientId,
  projectId,
  setToast,
  setHandleSave,
  setFilterButton,
  setTimesheetExporter,
}) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("activities");
  const [project, setProject] = useState({});
  const [range, setRange] = useState({});
  const [activities, setActivities] = useState([]);
  const [panelMinHeight, setPanelMinHeight] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [tourSteps, setTourSteps] = useState([]);
  const [sheetTourState, setSheetTourState] = useState({
    hasTechnician: false,
    sheetType: "real",
    hasVisibleMonths: false,
    hasActivitiesDetails: false,
    isOtherModalOpen: false,
  });
  const [otherModalCommand, setOtherModalCommand] = useState({
    id: 0,
    action: "close",
  });
  const panelRef = useRef(null);

  const responsibleEntity = useMemo(() => {
    const val = project?.management?.responsible_entity ?? "";
    return String(val).toUpperCase().trim();
  }, [project]);

  const isTheoretical = project.state === "THEORETICAL";
  const showSalariesTab =
    !!project.salary_access_allowed && !!project.project_is_salary_based;

  const investmentMethodology = useMemo(() => {
    const val = project?.management?.investment_methodology ?? "";
    return String(val).toUpperCase().trim();
  }, [project]);
  const hasSummaryTab = useMemo(
    () => responsibleEntity !== "OTHER",
    [responsibleEntity],
  );
  const TeamComponent = useMemo(
    () =>
      responsibleEntity === "IAPMEI"
        ? TeamIAPMEI
        : responsibleEntity === "ANI"
          ? TeamANI
          : TeamOTHER,
    [responsibleEntity],
  );
  const ActivitiesComponent = useMemo(
    () =>
      responsibleEntity === "IAPMEI"
        ? ActivitiesIAPMEI
        : responsibleEntity === "ANI"
          ? ActivitiesANI
          : ActivitiesOTHER,
    [responsibleEntity],
  );
  const SheetComponent = useMemo(
    () =>
      responsibleEntity === "IAPMEI"
        ? SheetIAPMEI
        : responsibleEntity === "ANI"
          ? SheetANI
          : SheetOTHER,
    [responsibleEntity],
  );
  const ImputationComponent = useMemo(
    () =>
      responsibleEntity === "IAPMEI"
        ? ImputationIAPMEI
        : responsibleEntity === "ANI"
          ? ImputationANI
          : ImputationOTHER,
    [responsibleEntity],
  );
  useEffect(() => {
    apiClient
      .get(`/projects/management/${clientId}/${projectId}/project/`)
      .then((response) => {
        const data = response.data;
        setProject(data);
        setRange({});
        setLoading(false);
      })
      .catch((error) => console.error("Erro ao carregar projeto:", error));
  }, [clientId, projectId]);

  useEffect(() => {
    setFilterButton(
      <FilterPanel project={project.management} onApply={setRange} />,
    );
  }, [project, setRange, setFilterButton]);

  const triggerDownloadFromUrl = useCallback((downloadUrl) => {
    window.location.assign(downloadUrl);
  }, []);

  const waitForTimesheetExport = useCallback(
    async (taskId) => {
      const maxAttempts = 60;
      const delayMs = 5000;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const resp = await apiClient.get(
          `/projects/management/${clientId}/export/task/${taskId}/`,
          {
            responseType: "json",
            validateStatus: () => true,
          },
        );

        if (resp.status === 202) {
          await new Promise((r) => setTimeout(r, delayMs));
          continue;
        }

        if (resp.status === 200 && resp.data?.download_url) {
          triggerDownloadFromUrl(resp.data.download_url);
          return;
        }

        throw new Error("Exportação falhou");
      }

      throw new Error("Tempo de espera esgotado");
    },
    [clientId, triggerDownloadFromUrl],
  );

  const extractTimesheet = useCallback(async () => {
    try {
      const startResp = await apiClient.get(
        `/projects/management/${clientId}/${projectId}/project/`,
        {
          params: { file: "excel" },
          responseType: "json",
        },
      );

      if (startResp.status === 200 && startResp.data?.download_url) {
        triggerDownloadFromUrl(startResp.data.download_url);
        return;
      }

      if (startResp.status === 202 && startResp.data?.task_id) {
        await waitForTimesheetExport(startResp.data.task_id);
        return;
      }

      throw new Error("Não foi possível iniciar a exportação");
    } catch (error) {
      setToast({ title: "Erro ao carregar projeto:", type: "error" });
      throw error;
    }
  }, [clientId, projectId, waitForTimesheetExport, triggerDownloadFromUrl]);

  useEffect(() => {
    if (!setTimesheetExporter) return;
    setTimesheetExporter(() => extractTimesheet);
    return () => setTimesheetExporter(null);
  }, [extractTimesheet, setTimesheetExporter]);

  const chartOptions = useMemo(
    () =>
      getGanttOptions(
        project.management?.startDate,
        project.management?.endDate,
        project.management?.executed,
        activities,
      ),
    [activities, project],
  );

  useLayoutEffect(() => {
    if (!panelRef.current) return;
    const height = panelRef.current.getBoundingClientRect().height;
    if (height > 0) setPanelMinHeight(height);
  }, [activeTab, loading, responsibleEntity]);

  useEffect(() => {
    if (typeof setHandleSave === "function" && activeTab !== "sheet" && activeTab !== "salaries") {
      setHandleSave(null);
    }
  }, [activeTab, setHandleSave]);

  const tourTabSequence = useMemo(() => {
    const base = ["activities", "team"];
    if (showSalariesTab) base.push("salaries");
    base.push("sheet");
    if (hasSummaryTab) base.push("kpi");
    return base;
  }, [hasSummaryTab, showSalariesTab]);

  const openTabTutorial = useCallback(
    (tab) => {
      const normalizedTab = tourTabSequence.includes(tab)
        ? tab
        : tourTabSequence[0];
      const baseSteps = getProjectTabTourSteps({
        tab: normalizedTab,
        showSummaryTab: hasSummaryTab,
        context: { ...sheetTourState, isTheoretical },
      });

      const nextSteps = baseSteps.map((step) => {
        return {
          selector: step.target,
          content: (
            <div>
              <div className="fw-semibold mb-1">{step.title}</div>
              <div>{step.content}</div>
            </div>
          ),
          position: step.placement || "bottom",
        };
      });

      if (!nextSteps.length) return;
      setActiveTab(normalizedTab);
      setTourStepIndex(0);
      setTourSteps(nextSteps);
      setRunTour(true);
    },
    [
      sheetTourState,
      isTheoretical,
      hasSummaryTab,
      tourTabSequence,
    ],
  );
  const handleStartTour = useCallback(() => {
    openTabTutorial(activeTab);
  }, [activeTab, openTabTutorial]);

  useEffect(() => {
    if (!runTour) return;
    const isOtherSheet =
      activeTab === "sheet" &&
      String(sheetTourState.sheetType || "").includes("other");
    if (!isOtherSheet) return;

    const selector = tourSteps?.[tourStepIndex]?.selector;
    const wantsModal =
      typeof selector === "string" &&
      selector.startsWith(".tour-other-activities");

    if (wantsModal && !sheetTourState.isOtherModalOpen) {
      setOtherModalCommand((prev) => ({ id: prev.id + 1, action: "open" }));
    } else if (!wantsModal && sheetTourState.isOtherModalOpen) {
      setOtherModalCommand((prev) => ({ id: prev.id + 1, action: "close" }));
    }
  }, [
    activeTab,
    runTour,
    sheetTourState.isOtherModalOpen,
    sheetTourState.sheetType,
    tourStepIndex,
    tourSteps,
  ]);

  const handleTourStepChange = useCallback(
    (nextStep) => {
      const resolvedStep =
        typeof nextStep === "function" ? nextStep(tourStepIndex) : nextStep;
      const isOtherSheet =
        activeTab === "sheet" &&
        String(sheetTourState.sheetType || "").includes("other");
      const nextSelector = tourSteps?.[resolvedStep]?.selector;
      const modalStep =
        typeof nextSelector === "string" &&
        nextSelector.startsWith(".tour-other-activities");

      if (isOtherSheet) {
        const shouldOpenModal = Boolean(modalStep);
        const shouldCloseModal = !modalStep;
        if (shouldOpenModal && !sheetTourState.isOtherModalOpen) {
          setOtherModalCommand((prev) => ({ id: prev.id + 1, action: "open" }));
        } else if (shouldCloseModal && sheetTourState.isOtherModalOpen) {
          setOtherModalCommand((prev) => ({
            id: prev.id + 1,
            action: "close",
          }));
        }
      }

      setTourStepIndex(resolvedStep);
    },
    [activeTab, sheetTourState, tourStepIndex, tourSteps],
  );

  useEffect(() => {
    if (!runTour) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [runTour]);
  const closeProjectTour = useCallback(() => {
    setRunTour(false);
    setTourStepIndex(0);
    setTourSteps([]);
    if (sheetTourState.isOtherModalOpen) {
      setOtherModalCommand((prev) => ({ id: prev.id + 1, action: "close" }));
    }
  }, [sheetTourState.isOtherModalOpen]);

  if (loading) {
    return <LoadingScreen message="A carregar projeto..." />;
  }

  return (
    <>
      <Tour
        isOpen={runTour}
        setIsOpen={(open) => {
          if (typeof open !== "boolean") return;
          setRunTour(open);
          if (!open) closeProjectTour();
        }}
        steps={tourSteps}
        currentStep={tourStepIndex}
        setCurrentStep={handleTourStepChange}
        onClickClose={({ setIsOpen }) => {
          setIsOpen(false);
          closeProjectTour();
        }}
        onClickMask={({ setIsOpen }) => {
          setIsOpen(false);
          closeProjectTour();
        }}
        showBadge={false}
        showNavigation
        showDots
        showPrevNextButtons
        showCloseButton
        closeWithMask
        disableInteraction={false}
      />

      {/* Cronograma */}
      <CCard className="mb-2 tour-project-gantt">
        <CCardHeader>
          <div className="d-flex justify-content-center align-items-center">
            <h4 className="m-0">{project.name}</h4>
          </div>
        </CCardHeader>
        <CCardBody>
          <HighchartsReact
            highcharts={Highcharts}
            style={{ maxHeight: 600, overflowY: "auto" }}
            options={chartOptions}
            constructorType={"ganttChart"}
          />
        </CCardBody>
      </CCard>

      {/* Tabs */}
      <CCard>
        <CTabs activeItemKey={activeTab} onChange={setActiveTab}>
          <div className="d-flex align-items-center justify-content-between gap-2 pe-3">
            <CTabList variant="tabs" className="mb-0 flex-grow-1">
              <CTab itemKey="activities" className="tour-project-tab-activities">
                Atividades
              </CTab>
              <CTab itemKey="team" className="tour-project-tab-team">
                Equipa
              </CTab>
              {showSalariesTab && (
                <CTab itemKey="salaries" className="tour-project-tab-salaries">
                  Salários
                </CTab>
              )}
              <CTab itemKey="sheet" className="tour-project-tab-sheet">
                Sheets
              </CTab>
              {hasSummaryTab && (
                <CTab itemKey="kpi" className="tour-project-tab-kpi">
                  Resumo
                </CTab>
              )}
            </CTabList>
            <CButton
              color="primary"
              variant="ghost"
              size="sm"
              className="d-inline-flex align-items-center gap-1 text-nowrap mb-2 px-2"
              onClick={handleStartTour}
            >
              <CIcon icon={cilInfo} />
              Tutorial
            </CButton>
          </div>
          <div ref={panelRef}>
            <CTabContent>
              <CTabPanel
                className="p-3 tour-project-activities-panel"
                itemKey="activities"
              >
                <Suspense
                  fallback={<PainelLoading minHeight={panelMinHeight} />}
                >
                  {activeTab === "activities" && (
                    <ActivitiesComponent
                      clientId={clientId}
                      projectId={projectId}
                      setToast={setToast}
                      onChange={setActivities}
                      setHandleSave={setHandleSave}
                      readOnly
                      projectState={project.state}
                    />
                  )}
                </Suspense>
              </CTabPanel>
              <CTabPanel className="p-3 tour-project-team-panel" itemKey="team">
                <Suspense
                  fallback={<PainelLoading minHeight={panelMinHeight} />}
                >
                  {activeTab === "team" && (
                    <TeamComponent
                      clientId={clientId}
                      projectId={projectId}
                      setToast={setToast}
                      setHandleSave={setHandleSave}
                      readOnly
                    />
                  )}
                </Suspense>
              </CTabPanel>
              {showSalariesTab && (
                <CTabPanel
                  className="p-3 tour-project-salaries-panel"
                  itemKey="salaries"
                >
                  <Suspense
                    fallback={<PainelLoading minHeight={panelMinHeight} />}
                  >
                    {activeTab === "salaries" && (
                      <TeamSalaries
                        clientId={clientId}
                        projectId={projectId}
                        setToast={setToast}
                        setHandleSave={setHandleSave}
                        pendingReview
                      />
                    )}
                  </Suspense>
                </CTabPanel>
              )}
              <CTabPanel
                className="p-3 tour-project-sheet-panel"
                itemKey="sheet"
              >
                <Suspense
                  fallback={<PainelLoading minHeight={panelMinHeight} />}
                >
                  {activeTab === "sheet" && (
                    <SheetComponent
                      clientId={clientId}
                      projectId={projectId}
                      setToast={setToast}
                      filterRange={range}
                      investmentMethodology={investmentMethodology}
                      setHandleSave={setHandleSave}
                      onTourSheetStateChange={setSheetTourState}
                      tourOtherModalCommand={otherModalCommand}
                      projectState={project.state}
                    />
                  )}
                </Suspense>
              </CTabPanel>
              <CTabPanel className="p-3 tour-project-kpi-panel" itemKey="kpi">
                <Suspense
                  fallback={<PainelLoading minHeight={panelMinHeight} />}
                >
                  {activeTab === "kpi" && (
                    <ImputationComponent
                      clientId={clientId}
                      projectId={projectId}
                      setToast={setToast}
                      filterRange={range}
                      setHandleSave={setHandleSave}
                    />
                  )}
                </Suspense>
              </CTabPanel>
            </CTabContent>
          </div>
        </CTabs>
      </CCard>
    </>
  );
};

export default Project;

const PainelLoading = ({ minHeight }) => (
  <div
    className="p-5 text-center"
    style={minHeight ? { minHeight } : undefined}
  >
    <CSpinner color="primary" style={{ width: "4rem", height: "4rem" }} />
    <CRow className="mt-3">
      <CCol className="text-center">
        <span className="text-muted fs-5 fw-semibold">A carregar página</span>
      </CCol>
    </CRow>
  </div>
);
